/**
 * Workspace-specific auth wrapper that initializes AuthContext with
 * workspace-scoped Keycloak client configuration.
 *
 * Access gating is handled entirely by the permission system
 * (PermissionProvider + PermissionGate / ProtectedRoute) — no
 * hardcoded role names per HLD-010.
 *
 * @module apps/workspace/AuthWrapper
 * @see TASK-FRONTEND-WS-AUTH-CHG-001
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "../../store/AuthContext";

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const KEYCLOAK_URL: string = import.meta.env.VITE_KEYCLOAK_URL || "";

const KEYCLOAK_REALM: string = import.meta.env.VITE_KEYCLOAK_REALM || "verisa";

const WORKSPACE_CLIENT_ID: string =
  import.meta.env.VITE_WORKSPACE_CLIENT_ID || "verisa-workspace";

// Construct the full Keycloak realm URL (same pattern as admin SPA).
const keycloakRealmUrl: string | undefined = KEYCLOAK_URL
  ? `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`
  : undefined;

const LOGIN_PATH = "/login";

// ---------------------------------------------------------------------------
// RequireAuth -- inner guard that redirects unauthenticated users
// ---------------------------------------------------------------------------

interface RequireAuthProps {
  children: React.ReactNode;
}

function RequireAuth({ children }: RequireAuthProps): React.ReactElement {
  const { authenticated, initialized } = useAuth();
  const location = useLocation();

  if (!initialized) {
    return <></>;
  }

  if (!authenticated) {
    return <Navigate to={LOGIN_PATH} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// WorkspaceAuthWrapper -- configures AuthProvider for workspace scope
// ---------------------------------------------------------------------------

export interface WorkspaceAuthWrapperProps {
  children: React.ReactNode;
}

/**
 * Wraps workspace routes with an AuthProvider configured for the workspace
 * Keycloak client. All children can use `useAuth()` to access
 * workspace-scoped authentication state and actions.
 */
export function WorkspaceAuthWrapper({
  children,
}: WorkspaceAuthWrapperProps): React.ReactElement {
  return (
    <AuthProvider keycloakUrl={keycloakRealmUrl} clientId={WORKSPACE_CLIENT_ID}>
      {children}
    </AuthProvider>
  );
}

// Re-export RequireAuth for use in route definitions
export { RequireAuth };

export default WorkspaceAuthWrapper;
